class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.searchSuggestions = this.querySelector("._searchSuggestions");
    this.isOpen = false;

    this.placeHolderTexts = window.searchBar.dynamicPrompts.filter(
      (item) => item != ""
    );
    this.placeHolderIndex = 0;
    this.placeHolderTextLetterIndex = 0;
    this.searchBar = this.querySelector("#Search-In-Modal");
    this.searchBar.setAttribute("placeholder", window.searchBar.staticPrompt);

    if (this.placeHolderTexts.length > 1) {
      this.typeWriterEffect(this.placeHolderTexts[0]);
    } else {
      this.searchBar.setAttribute(
        "placeholder",
        window.searchBar.staticPrompt + this.placeHolderTexts
      );
    }

    this.setupEventListeners();
    document.addEventListener("keydown", this.addShortcut.bind(this));
  }

  typeWriterEffect(string) {
    if (this.placeHolderTextLetterIndex < string.length) {
      const newAttribute =
        this.searchBar.getAttribute("placeholder") +
        string.charAt(this.placeHolderTextLetterIndex);
      this.searchBar.setAttribute("placeholder", newAttribute);
      this.placeHolderTextLetterIndex++;
      setTimeout(this.typeWriterEffect.bind(this, string), 75);
    } else {
      setTimeout(this.typeDeleterEffect.bind(this, string), 1000);
    }
  }

  typeDeleterEffect(string) {
    if (
      this.searchBar.getAttribute("placeholder") !=
      window.searchBar.staticPrompt
    ) {
      const newAttribute = this.searchBar
        .getAttribute("placeholder")
        .slice(0, -1);
      this.searchBar.setAttribute("placeholder", newAttribute);
      setTimeout(this.typeDeleterEffect.bind(this, string), 50);
    } else {
      this.placeHolderIndex =
        (this.placeHolderIndex + 1) % this.placeHolderTexts.length;
      this.placeHolderTextLetterIndex = 0;
      this.typeWriterEffect(this.placeHolderTexts[this.placeHolderIndex]);
    }
  }

  setupEventListeners() {
    const form = this.querySelector('form[role="search"]');
    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    form.addEventListener("click", () => this.input.focus());
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showSuggestions();
      this.querySelector("._notFoundMessage").style.display = "";
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  showSuggestions() {
    this.parentElement.classList.add("lg:relative", "z-[1000]");
    this.predictiveSearchResults.classList.add("hidden");
    this.searchSuggestions.classList.remove("hidden");
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
  }

  hideSuggestions() {
    this.parentElement.classList.remove("absolute", "lg:relative", "z-[1000]");
    this.searchSuggestions.classList.add("hidden");
  }

  onFocus() {
    lockScrollForThis();
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showSuggestions();
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut(e) {
    unlockScrollForThis();
    if (
      e.target.getAttribute("aria-label") === "minus" ||
      e.target.getAttribute("aria-label") === "delete"
    )
      return;
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    event.preventDefault();

    switch (event.code) {
      case "Escape":
        this.close();
        break;
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&${encodeURIComponent(
        "resources[type]"
      )}=product,collection&section_id=api--search--predictive`
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-api--search--predictive");

        // check if no results
        const noResultsItem = resultsMarkup.querySelector("#no-results");
        const noResultsText = document.querySelector("._notFoundMessage");
        this.noResults = false;
        if (noResultsItem) {
          this.noResults = true;
          noResultsText.innerHTML = noResultsItem.innerHTML;
        } else {
          this.cachedResults[queryKey] = resultsMarkup.innerHTML;
          noResultsText.innerHTML = "";
        }

        this.renderSearchResults(resultsMarkup.innerHTML);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.noResults) {
      this.querySelector("._notFoundMessage").style.display = "block";
    } else {
      this.hideSuggestions();
      this.querySelector("._notFoundMessage").style.display = "";
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute("results", true);
      this.setLiveRegionResults();
      this.open();
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  open() {
    this.parentElement.classList.add("absolute", "lg:relative", "z-[1000]");
    this.predictiveSearchResults.classList.remove("hidden");
    this.predictiveSearchResults.classList.remove("opacity-0");
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.hideSuggestions();
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      this.predictiveSearchResults.innerHTML = "";
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);

    this.isOpen = false;
    this.predictiveSearchResults.classList.add("hidden");
  }

  addShortcut(event) {
    // Check if the event is triggered by Command (Mac) or Ctrl (Windows)
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    const key = isMac ? "Meta" : "Control";

    // Check if the 'K' key is pressed
    if (
      event.key === "/" ||
      (event.key === "k" && (event.metaKey || event.ctrlKey))
    ) {
      // Prevent the default behavior of the keypress (e.g., Ctrl+K in the browser)
      event.preventDefault();
      // Focus on the 'search' element
      this.input.focus();
    }
  }
}

customElements.define("predictive-search", PredictiveSearch)
